/**
 * Product display-image resolution — the shared, site-wide primitive for picking the *right*
 * catalog image to show for a product (tiles, carousels, mini-cart, upsell, etc.) and turning it
 * into a `DynamicImage`-ready, correctly-sized `src`, with a graceful "no image" fallback signal.
 *
 * ## View-type fallback rule (per TSD)
 *
 * SCAPI exposes a product's imagery as `imageGroups` keyed by `viewType` (`large`, `medium`,
 * `small`, `hi-res`, …). Catalogs aren't guaranteed to configure every view type for every
 * product, so naively reading a single `viewType` (the historical Want It Sooner bug — it only
 * read `large`) renders a broken/empty image when that one type is missing.
 *
 * The documented device preference is:
 *
 *   - **Desktop** → `large`, fallback to `hi-res`
 *   - **Tablet**  → `medium`, fallback to `large`
 *   - **Mobile**  → `small`, fallback to `medium`
 *   - **All**     → ultimately fall back to `hi-res`
 *
 * These per-device chains are exported as {@link DISPLAY_VIEWTYPE_FALLBACK} for callers that want
 * to express the rule literally. However, King Living images are served by Scene7 DIS / Cylindo,
 * where the *rendered* pixel size is governed by the URL resize param (`sw=` / `size=`) rather
 * than by which view-type group a URL came from — and `DynamicImage` already varies that param
 * per breakpoint via `srcSet`. So for the common single-`<DynamicImage>` render path we resolve a
 * single best base asset using the unified {@link DISPLAY_VIEWTYPE_PRIORITY} (the union of the
 * device chains, ordered `large → hi-res → medium → small`): this prefers the desktop primary,
 * then the universal `hi-res` fallback, then the smaller types as a last resort, guaranteeing we
 * surface *whatever* is configured. The browser then downloads a breakpoint-appropriate size.
 *
 * ## Source priority (PIM-first)
 *
 * Within each view type we mirror {@link ./product-images.js}: prefer King Living's
 * `c_processedImageGroups` (PIM-aware, `imageSource: 'pim'` curated photography) over the legacy
 * SCAPI `imageGroups`, and master groups (no `variationAttributes`) over variant-scoped ones, so a
 * tile renders the same hero asset as the PDP it links to.
 *
 * ## No image configured
 *
 * When nothing resolves, {@link getDisplayImages} returns an empty array — callers should render
 * the shared `<ImageNotAvailable>` placeholder (same affordance used on PLP tiles and the PDP
 * gallery) rather than an empty/broken `<img>`.
 */
import {parseProcessedImageGroups} from '@amblique/king-living/app/utils/cylindo-params';
import {buildDynamicImageSrc} from '@amblique/king-living/app/utils/responsive-image-src';

/**
 * Per-device view-type fallback chains (each terminates at `hi-res`, the universal fallback).
 * Exported for callers/tests that need the documented business rule verbatim.
 */
export const DISPLAY_VIEWTYPE_FALLBACK = Object.freeze({
  desktop: ['large', 'hi-res'],
  tablet: ['medium', 'large', 'hi-res'],
  mobile: ['small', 'medium', 'hi-res'],
});

/**
 * Unified view-type priority for a single-source `<DynamicImage>` render. Union of the device
 * chains, ordered so the highest-quality desktop asset wins and `hi-res` is the universal
 * fallback before the smaller types. The actual displayed size is handled per-breakpoint by the
 * DIS/Cylindo resize param, so one base asset serves every device correctly.
 */
export const DISPLAY_VIEWTYPE_PRIORITY = ['large', 'hi-res', 'medium', 'small'];

const hasImages = (group) => Array.isArray(group?.images) && group.images.length > 0;

/**
 * Pick a master group of a given view type from the PIM-aware `c_processedImageGroups`,
 * preferring curated `pim` photography over Cylindo frame snapshots.
 * @param {object} shape
 * @param {string} viewType
 * @returns {object|null}
 */
const pickProcessedGroup = (shape, viewType) => {
  const groups = parseProcessedImageGroups(shape).filter(
    (g) => g?.viewType === viewType && !g.variationAttributes
  );

  return groups.find((g) => g.imageSource === 'pim' && hasImages(g)) || groups.find(hasImages) || null;
};

/**
 * Pick a group of a given view type from the legacy SCAPI `imageGroups`,
 * master (no `variationAttributes`) first, then any group of that type.
 * @param {object} shape
 * @param {string} viewType
 * @returns {object|null}
 */
const pickLegacyGroup = (shape, viewType) => {
  if (!Array.isArray(shape?.imageGroups)) return null;

  const master = shape.imageGroups.find(
    (g) => g?.viewType === viewType && !g.variationAttributes && hasImages(g)
  );

  if (master) return master;

  return shape.imageGroups.find((g) => g?.viewType === viewType && hasImages(g)) || null;
};

/**
 * Walk the view-type priority for one product-shaped object, trying PIM-first then legacy at each
 * step so an earlier view type always wins over a later one (`large` beats `hi-res`).
 * @param {object} shape
 * @param {string[]} viewTypePriority
 * @returns {object|null}
 */
const pickGroupForShape = (shape, viewTypePriority) => {
  if (!shape || typeof shape !== 'object') return null;

  for (const viewType of viewTypePriority) {
    const group = pickProcessedGroup(shape, viewType) || pickLegacyGroup(shape, viewType);

    if (group) return group;
  }

  return null;
};

/**
 * Resolve the best display image *group* for a product following the view-type fallback rule.
 *
 * `productSearch` hits often nest `c_processedImageGroups` / `imageGroups` on `representedProduct`
 * only; full product responses carry them on the root. We try both so every surface stays
 * consistent.
 *
 * @param {object|null|undefined} product
 * @param {object} [opts]
 * @param {string[]} [opts.viewTypePriority=DISPLAY_VIEWTYPE_PRIORITY]
 * @returns {object|null} matched group (with `images`, `viewType`, `imageSource`), or `null`.
 */
export const resolveDisplayImageGroup = (
  product,
  {viewTypePriority = DISPLAY_VIEWTYPE_PRIORITY} = {}
) => {
  if (!product || typeof product !== 'object') return null;

  return (
    pickGroupForShape(product, viewTypePriority) ||
    pickGroupForShape(product.representedProduct, viewTypePriority)
  );
};

/**
 * Render-ready display images: best view type per the fallback rule, video stripped, URL present.
 * Returns an empty array when no imagery is configured — render `<ImageNotAvailable>` in that case.
 *
 * @param {object|null|undefined} product
 * @param {object} [opts] - see {@link resolveDisplayImageGroup}.
 * @returns {Array<object>} image objects (`{disBaseLink, link, alt, …}`).
 */
export const getDisplayImages = (product, opts) => {
  const group = resolveDisplayImageGroup(product, opts);
  const images = group?.images ?? [];

  return images
    .filter((img) => img.mediaType !== 'video')
    .filter((img) => img.disBaseLink || img.link);
};

/**
 * Convenience: the un-sized base URL for a resolved image object (`disBaseLink` preferred).
 * @param {object|null|undefined} image
 * @returns {string}
 */
export const getDisplayImageUrl = (image) => image?.disBaseLink || image?.link || '';

/**
 * Convenience: a `DynamicImage`-ready `src` for a resolved image object. Applies the responsive
 * `size={width}` (Cylindo) / serve-as-is (PIM) policy via {@link buildDynamicImageSrc}. The
 * per-breakpoint sizing comes from the `widths` prop passed to `<DynamicImage>` at the call site.
 *
 * @param {object|null|undefined} image
 * @returns {string} src usable by `<DynamicImage>` — empty string when no URL.
 */
export const getDisplayImageSrc = (image) =>
  buildDynamicImageSrc(getDisplayImageUrl(image));
